import type { ReactNode } from 'react';
import {
    CountedTextInput,
    FormField,
    InputGroup,
    Paragraph,
    TextInput,
} from '@george-labs.com/design-system';
import type { CountedTextInputProps } from '@george-labs.com/design-system/types/components/Input';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props<FormValues>
    extends FormFieldProps<FormValues>,
        Omit<CountedTextInputProps<HTMLInputElement>, 'input'> {
    autoFocus?: boolean;
    prepend?: ReactNode;
    append?: ReactNode;
    type?: 'text' | 'password';
    maxLength?: number;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

const LABEL_COLUMN_WIDTH = 4;

export const FormCountedTextField = <FormValues = void,>({
    label,
    placeholder,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelPosition,
    fieldName,
    required,
    disabled,
    warning,
    info,
    alert,
    autoFocus,
    append,
    prepend,
    description,
    type,
    maxChars,
    maxLength,
    message,
    limitInputToMaxChars,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
}: Props<FormValues>) => {
    const [fieldProps, { error, touched }] = useField(fieldName, { shouldClearOnUnmount });

    const isFieldErrorVisible = shouldShowError(isErrorVisible, touched, error);

    return (
        <FormField
            description={description}
            required={required}
            input={
                <InputGroup disabled={disabled} append={append} prepend={prepend}>
                    <CountedTextInput
                        maxChars={maxChars}
                        message={message}
                        limitInputToMaxChars={limitInputToMaxChars}
                        input={
                            <TextInput
                                type={type}
                                autoFocus={autoFocus}
                                data-cy={dataCy}
                                maxLength={maxLength}
                                placeholder={placeholder}
                                required={required}
                                {...fieldProps}
                            />
                        }
                    />
                </InputGroup>
            }
            label={label}
            placeholder={placeholder}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            labelPosition={labelPosition}
            data-cy={dataCy}
            disabled={disabled}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
        />
    );
};

FormCountedTextField.LABEL_POSITION = FormField.LABEL_POSITION;
FormCountedTextField.TYPE = TextInput.TYPE;
